<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="selectedIndex"
    ref="gc-base-layout"
    class="touch-sales-order-layout"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Product"
        :baseView="baseView"
        :createModeIsEditMode="true"
        :primaryLabel="model.ProductCode"
        :secondaryLabel="model.Description1"
        recordType="Product"
        v-if="model.ProductCode"
      />
    </template>
    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="product-media-tab">
        <div class="pmt-panel">
          <div class="pmt-toolbar" v-if="canWrite">
            <DxButton
              text="Pick Existing"
              icon="find"
              type="normal"
              styling-mode="outlined"
              @click="openPickExisting"
            />
          </div>

          <div
            v-if="canWrite"
            class="pmt-drop-zone"
            :class="{ 'is-dragging': isDragging }"
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              style="display:none"
              @change="onFilesSelected"
            />
            <svg viewBox="0 0 24 24"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
            Drop files here or <span class="dz-link">browse</span> — JPEG, PNG, PDF, Word, Excel, CSV, TXT
            <span class="dz-hint">10 MB each</span>
          </div>

          <div v-if="errorMsg" class="pmt-error-msg">{{ errorMsg }}</div>

          <div class="pmt-gallery-section">
            <div class="pmt-gallery-heading">Images</div>
            <div class="pmt-gallery pmt-gallery-images">
              <div
                v-for="attachment in imageAttachments"
                :key="attachment.ProductMediaFileRecordID"
                class="pmt-tile pmt-tile-image"
                @click="openPreview(attachment)"
              >
                <img :src="getThumbUrl(attachment.ProductMediaFile)" :alt="attachment.ProductMediaFile.FileName" />
                <span
                  v-if="canWrite"
                  class="pmt-star-toggle"
                  :class="{ 'is-primary': attachment.IsPrimary === 'Y' }"
                  title="Set as Primary image"
                  @click.stop="setPrimary(attachment)"
                >
                  <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </span>
                <span
                  v-else-if="attachment.IsPrimary === 'Y'"
                  class="pmt-star-toggle is-primary pmt-star-static"
                  title="Primary image"
                >
                  <svg viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </span>
                <span
                  v-if="canWrite"
                  class="pmt-remove-icon"
                  title="Remove"
                  @click.stop="removeAttachment(attachment)"
                >
                  <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </span>
              </div>
              <div v-if="imageAttachments.length === 0" class="pmt-gallery-empty">
                No images yet.
              </div>
            </div>
          </div>

          <div class="pmt-gallery-section">
            <div class="pmt-gallery-heading">Documents</div>
            <div class="pmt-gallery pmt-gallery-documents">
              <div
                v-for="attachment in documentAttachments"
                :key="attachment.ProductMediaFileRecordID"
                class="pmt-tile pmt-tile-document"
                @click="openPreview(attachment)"
              >
                <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span class="pmt-tile-filename">{{ attachment.ProductMediaFile.FileName }}</span>
                <span
                  v-if="canWrite"
                  class="pmt-remove-icon"
                  title="Remove"
                  @click.stop="removeAttachment(attachment)"
                >
                  <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </span>
              </div>
              <div v-if="documentAttachments.length === 0" class="pmt-gallery-empty">
                No documents yet.
              </div>
            </div>
          </div>
        </div>
      </div>

      <gc-popup-product-media-pick-existing
        v-if="pickExistingVisible"
        :visible="pickExistingVisible"
        :product-code="model.ProductCode"
        :display-server-error="displayServerError"
        @close="closePickExisting"
        @selected="onFilesAssociated"
      />

      <GcPopupImagePreview
        v-if="previewVisible"
        :preview-visible="previewVisible"
        :on-back-button-click="closePreview"
        :data-model="previewDataModel"
        :content-type="previewAttachment && getOriginalContentType(previewAttachment.ProductMediaFile)"
        :allow-download="true"
        :allow-remove="canWrite"
        :remove-confirm-message="previewRemoveMessage"
        :on-remove-image="() => detachAttachment(previewAttachment)"
      />
    </div>
  </GcBaseLayout>
</template>

<script>
import BaseViewMixin from "../../v1/_mixins/base.view";
import {
  ProductMediaService,
  isImageFile,
  getThumbUrl,
  getPreviewUrl,
  getOriginalContentType,
} from "../../_services/api/products/product-media.service";
import { ProductTabs } from "../../_models/tabdata/products/products-tabs";
import gcPopupProductMediaPickExisting from "../../popups/products/gc-popup-product-media-pick-existing.vue";
import appConfig from "../../app-config";
import { FeatureStorage } from "../../_services/storage";

// Matches complaint-attachments-tab.vue's allow-list/size limit exactly (BR-2092-1).
var MAX_FILE_SIZE = 10 * 1024 * 1024;
var ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
];

export default {
  mixins: [BaseViewMixin],
  components: {
    "gc-popup-product-media-pick-existing": gcPopupProductMediaPickExisting,
  },
  created() {
    // Media sits between Dimensions (5) and Kit Components (7) in ProductTabs()
    this.selectedIndex = 6;
    this.initialized();
  },
  data() {
    return {
      model: {
        ProductCode: "",
        Description1: "",
      },
      loaded: false,
      isDragging: false,
      errorMsg: "",
      errorTimer: null,
      attachments: [],
      pickExistingVisible: false,
      previewVisible: false,
      previewAttachment: null,
      previewRemoveMessage: "",
    };
  },
  computed: {
    getTabs() {
      return ProductTabs(this.baseView.id);
    },
    canWrite() {
      return FeatureStorage.checkPermission("ProductMedia", "edit");
    },
    previewDataModel() {
      if (!this.previewAttachment) return {};
      var file = this.previewAttachment.ProductMediaFile;
      return {
        PreviewImageSrc: getPreviewUrl(file),
        FileName: file.FileName,
        DownloadApiUrl: `${appConfig.VUE_APP_ROOT_API}/api/ProductMediaFile/${this.previewAttachment.ProductMediaFileRecordID}/Download?sizeType=original`,
      };
    },
    imageAttachments() {
      return this.attachments.filter(function (a) {
        return a.ProductMediaFile && isImageFile(a.ProductMediaFile);
      });
    },
    documentAttachments() {
      return this.attachments.filter(function (a) {
        return !a.ProductMediaFile || !isImageFile(a.ProductMediaFile);
      });
    },
  },
  methods: {
    getThumbUrl,
    getPreviewUrl,
    getOriginalContentType,
    backButtonFunction() {
      this.$router.push({
        path: "/product",
      });
    },
    async initialized() {
      await ProductMediaService.getProductByCode(this.baseView.id).then(
        (response) => {
          if (response.data.value.length > 0) {
            this.model.ProductCode = response.data.value[0].Code;
            this.model.Description1 = response.data.value[0].Description1;
          }
        }
      );
      await this.fetchAttachments();
      this.loaded = true;
    },
    async fetchAttachments() {
      await ProductMediaService.getAttachments(this.model.ProductCode).then(
        (response) => {
          this.attachments = response.data.value;
        }
      );
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFilesSelected(e) {
      var files = Array.from(e.target.files);
      this.processFiles(files);
      e.target.value = "";
    },
    onDrop(e) {
      this.isDragging = false;
      var files = Array.from(e.dataTransfer.files);
      this.processFiles(files);
    },
    processFiles(files) {
      var self = this;
      files.forEach(function (file) {
        if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
          self.showError(
            '"' +
              file.name +
              '" is not a supported file type. Please upload JPEG, PNG, PDF, Word, Excel, CSV, or TXT.'
          );
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          self.showError('"' + file.name + '" exceeds the 10 MB limit.');
          return;
        }
        self.readFileAsBase64(file);
      });
    },
    readFileAsBase64(file) {
      var self = this;
      var reader = new FileReader();
      reader.onload = function (e) {
        self.uploadFile({
          FileName: file.name,
          ContentType: file.type,
          FileStringBase64: e.target.result,
          ProductCode: self.model.ProductCode,
        });
      };
      reader.readAsDataURL(file);
    },
    async uploadFile(payload) {
      await ProductMediaService.uploadFile(payload)
        .then(() => {
          this.fetchAttachments();
        })
        .catch((err) => {
          this.displayServerError(err, "Upload Failed");
        });
    },
    // Immediate, no confirmation dialog (BR-2092-3). Optimistic UI update,
    // rolled back on error.
    async setPrimary(attachment) {
      if (attachment.IsPrimary === "Y") return;

      var previousPrimary = this.imageAttachments.find(function (a) {
        return a.IsPrimary === "Y";
      });

      this.imageAttachments.forEach(function (a) {
        a.IsPrimary = "N";
      });
      attachment.IsPrimary = "Y";

      await ProductMediaService.setPrimary({
        ProductCode: this.model.ProductCode,
        ProductMediaFileRecordID: attachment.ProductMediaFileRecordID,
      }).catch((err) => {
        this.imageAttachments.forEach(function (a) {
          a.IsPrimary = "N";
        });
        if (previousPrimary) previousPrimary.IsPrimary = "Y";
        this.displayServerError(err, "Set Primary Failed");
      });
    },
    // Wording is computed live at the moment Remove is clicked/Preview is opened,
    // not cached on the attachment row - two users could be detaching the same
    // shared file from different products concurrently.
    async buildRemoveConfirmMessage(attachment) {
      var otherProductsCount = 0;
      await ProductMediaService.getAssociationCount(
        attachment.ProductMediaFileRecordID
      ).then((response) => {
        var totalCount = response.data["@odata.count"] || 0;
        otherProductsCount = totalCount > 0 ? totalCount - 1 : 0;
      });

      return otherProductsCount > 0
        ? "This file is also used by " +
            otherProductsCount +
            (otherProductsCount === 1 ? " other product" : " other products") +
            ". Removing it here only detaches it from this product."
        : "This will remove the attachment. The underlying file will be deleted, since no other product uses it.";
    },
    async removeAttachment(attachment) {
      var message = await this.buildRemoveConfirmMessage(attachment);
      var confirmed = await this.$confirm(message, "Remove Attachment");
      if (!confirmed) return;

      await this.detachAttachment(attachment);
    },
    async detachAttachment(attachment) {
      await ProductMediaService.detach({
        ProductCode: this.model.ProductCode,
        ProductMediaFileRecordID: attachment.ProductMediaFileRecordID,
      })
        .then(() => {
          this.attachments = this.attachments.filter(function (a) {
            return (
              a.ProductMediaFileRecordID !== attachment.ProductMediaFileRecordID
            );
          });
        })
        .catch((err) => {
          this.displayServerError(err, "Remove Failed");
        });
    },
    async openPreview(attachment) {
      this.previewAttachment = attachment;
      this.previewRemoveMessage = await this.buildRemoveConfirmMessage(attachment);
      this.previewVisible = true;
    },
    closePreview() {
      this.previewVisible = false;
      this.previewAttachment = null;
    },
    openPickExisting() {
      this.pickExistingVisible = true;
    },
    closePickExisting() {
      this.pickExistingVisible = false;
    },
    onFilesAssociated() {
      this.closePickExisting();
      this.fetchAttachments();
    },
    showError(msg) {
      var self = this;
      self.errorMsg = msg;
      clearTimeout(self.errorTimer);
      self.errorTimer = setTimeout(function () {
        self.errorMsg = "";
      }, 5000);
    },
  },
  beforeDestroy() {
    clearTimeout(this.errorTimer);
  },
};
</script>

<style lang="scss">
.product-media-tab {
  .css-v2.gc-popup-form {
    padding-bottom: 12px;
  }

  .pmt-panel {
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 2px;
    padding: 20px;
  }

  .pmt-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 14px;
  }

  .pmt-drop-zone {
    border: 1px dashed #d0d5dd;
    border-radius: 2px;
    padding: 20px 16px;
    text-align: center;
    color: #667085;
    font-size: 12px;
    cursor: pointer;
    background: #fff;
    margin-bottom: 18px;
    transition: background 0.1s, border-color 0.1s;

    &:hover,
    &.is-dragging {
      background: #f0f9ff;
      border-color: #35baf6;
    }

    svg {
      width: 20px;
      height: 20px;
      stroke: #98a2b3;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      display: block;
      margin: 0 auto 8px;
    }

    .dz-link {
      color: #35baf6;
      font-weight: 500;
    }

    .dz-hint {
      display: block;
      font-size: 11px;
      color: #98a2b3;
      margin-top: 4px;
    }
  }

  .pmt-error-msg {
    font-size: 12px;
    padding: 6px 0 8px;
    color: #dc2626;
  }

  .pmt-gallery-section {
    margin-top: 16px;
  }

  .pmt-gallery-heading {
    font-size: 13px;
    font-weight: 600;
    color: #101828;
    margin-bottom: 8px;
  }

  .pmt-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .pmt-gallery-empty {
    font-size: 12px;
    color: #98a2b3;
    font-style: italic;
    padding: 8px 0;
  }

  .pmt-tile {
    position: relative;
    width: 120px;
    height: 120px;
    border: 1px solid #eaecf0;
    border-radius: 6px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .pmt-tile-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .pmt-star-toggle {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.1s;

    svg {
      width: 14px;
      height: 14px;
      stroke: #d0d5dd;
      fill: none;
      stroke-width: 1.5;
    }

    &.is-primary {
      opacity: 1;

      svg {
        stroke: #f79009;
        fill: #f79009;
      }
    }

    &.pmt-star-static {
      cursor: default;
    }
  }

  .pmt-tile-image:hover .pmt-star-toggle {
    opacity: 1;
  }

  .pmt-remove-icon {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.1s, background 0.1s, color 0.1s;
    color: #98a2b3;

    svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    &:hover {
      background: #fef2f2;
      color: #dc2626;
    }
  }

  .pmt-tile:hover .pmt-remove-icon {
    opacity: 1;
  }

  .pmt-tile-document {
    position: relative;

    .pmt-remove-icon {
      top: 4px;
      left: auto;
      right: 4px;
    }
  }

  .pmt-tile-document {
    flex-direction: column;
    gap: 6px;
    padding: 8px;

    svg {
      width: 32px;
      height: 32px;
      stroke: #667085;
      fill: none;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .pmt-tile-filename {
      font-size: 11px;
      color: #475467;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }
}
</style>
